import TemplatePreview from '@/components/TemplatePreview';
import { useToast } from '@/hooks/useToast';
import { mediaService } from '@/services/mediaService';
import { whatsappConfigService } from '@/services/whatsappConfigService';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

const SendTemplateModal = ({
  isOpen,
  onClose,
  templates,
  onSend,
  projectId,
}) => {
  const { showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [headerParams, setHeaderParams] = useState({});
  const [bodyParams, setBodyParams] = useState({});
  const [buttonParams, setButtonParams] = useState({});
  const [headerFile, setHeaderFile] = useState(null);
  const [errors, setErrors] = useState({});

  // Fetch WhatsApp configuration for media upload
  const { data: whatsappConfigData } = useQuery({
    queryKey: ['whatsapp-config', projectId],
    queryFn: () => whatsappConfigService.getWhatsAppConfig(projectId),
    enabled: !!projectId && isOpen,
  });

  const whatsappConfig = whatsappConfigData?.data?.whatsappConfig;
  const phoneNumberId = whatsappConfig?.phoneNumberId;

  const parseTemplateComponents = useCallback(template => {
    // Reset all params first
    setHeaderParams({});
    setBodyParams({});
    setButtonParams({});

    // Parse header
    if (template.header?.text) {
      const matches = template.header.text.match(/\{\{(\d+)\}\}/g);
      if (matches) {
        const params = {};
        matches.forEach(match => {
          const index = match.replace(/[{}]/g, '');
          params[index] = '';
        });
        setHeaderParams(params);
      }
    }

    // Parse body
    if (template.body?.text) {
      const matches = template.body.text.match(/\{\{(\d+)\}\}/g);
      if (matches) {
        const params = {};
        matches.forEach(match => {
          const index = match.replace(/[{}]/g, '');
          params[index] = '';
        });
        setBodyParams(params);
      }
    }

    // Parse buttons
    if (template.buttons && Array.isArray(template.buttons)) {
      const params = {};
      template.buttons.forEach((button, btnIndex) => {
        if (button.type === 'copy_code') {
          params[`copy_code_${btnIndex}`] = '';
        }
        if (
          button.type === 'url' &&
          (button.url?.includes('{{') || (button.example && button.example[0]))
        ) {
          params[`url_${btnIndex}`] = '';
        }
      });
      setButtonParams(params);
    }
  }, []);

  const handleTemplateChange = templateId => {
    const template = templates.find(t => t._id === templateId);
    setSelectedTemplate(template);
    setHeaderFile(null);
    setErrors({});
    if (template) {
      parseTemplateComponents(template);
    }
  };

  const handleHeaderFileChange = e => {
    const file = e.target.files[0];
    if (file && selectedTemplate?.header?.format) {
      const format = selectedTemplate.header.format;
      let valid = false;
      let maxSize = 0;

      if (format === 'IMAGE') {
        valid = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
        maxSize = 5 * 1024 * 1024;
      } else if (format === 'VIDEO') {
        valid = ['video/mp4', 'video/3gpp'].includes(file.type);
        maxSize = 16 * 1024 * 1024;
      } else if (format === 'DOCUMENT') {
        valid = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ].includes(file.type);
        maxSize = 100 * 1024 * 1024;
      }

      if (!valid) {
        showError(`Invalid file type for ${format}`);
        return;
      }
      if (file.size > maxSize) {
        showError(`File size exceeds ${(maxSize / 1024 / 1024).toFixed(0)}MB`);
        return;
      }
      setHeaderFile(file);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!selectedTemplate) return;

    // Basic validation
    const newErrors = {};
    if (
      ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(
        selectedTemplate.header?.format
      ) &&
      !headerFile
    ) {
      newErrors.headerFile = 'Header file is required';
    }
    Object.keys(headerParams).forEach(k => {
      if (!headerParams[k]) newErrors[`headerParam_${k}`] = 'Required';
    });
    Object.keys(bodyParams).forEach(k => {
      if (!bodyParams[k]) newErrors[`bodyParam_${k}`] = 'Required';
    });
    Object.keys(buttonParams).forEach(k => {
      if (!buttonParams[k]) newErrors[`buttonParam_${k}`] = 'Required';
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      let headerMediaId = null;
      let key = null;
      if (headerFile) {
        if (!phoneNumberId) throw new Error('WhatsApp configuration missing');
        const mediaResponse = await mediaService.uploadWhatsAppMedia(
          projectId,
          phoneNumberId,
          headerFile
        );
        headerMediaId = mediaResponse?.data?.id;
        key = mediaResponse?.data?.key;
      }

      const dynamicValues = {
        header: {},
        body: [],
        buttons: [],
      };

      if (selectedTemplate.header) {
        if (selectedTemplate.header.format === 'TEXT' && headerParams['1']) {
          dynamicValues.header = {
            value: headerParams['1'],
            fallback: headerParams['1'],
          };
        } else if (headerMediaId && key) {
          dynamicValues.header = { url: headerMediaId, key };
        }
      }

      Object.keys(bodyParams)
        .sort((a, b) => Number(a) - Number(b))
        .forEach(k => {
          dynamicValues.body.push({
            value: bodyParams[k],
            fallback: bodyParams[k],
          });
        });

      Object.keys(buttonParams).forEach(k => {
        const parts = k.split('_');
        const index = Number(parts[parts.length - 1]);
        const type = parts.slice(0, -1).join('_');
        dynamicValues.buttons.push({ index, value: buttonParams[k], type });
      });

      await onSend(selectedTemplate._id, dynamicValues);
      onClose();
    } catch (error) {
      showError(error.message || 'Failed to send template');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="flex flex-col md:flex-row h-full">
            {/* Left Column: Form */}
            <div className="md:w-3/5 p-6 border-r border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Send Template Message
              </h3>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Template
                  </label>
                  <select
                    className="w-full h-12 px-4 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-all bg-white"
                    onChange={e => handleTemplateChange(e.target.value)}
                    value={selectedTemplate?._id || ''}
                  >
                    <option value="">Select a template...</option>
                    {templates.map(t => (
                      <option key={t._id} value={t._id}>
                        {t.name} ({t.language})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTemplate && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="pt-4 border-t border-gray-100">
                      <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center">
                        <span className="w-1 h-4 bg-primary-500 rounded-full mr-2"></span>
                        Message Configuration
                      </h4>

                      <div className="space-y-5">
                        {/* Header Media Upload */}
                        {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(
                          selectedTemplate.header?.format
                        ) && (
                          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                              Header{' '}
                              {selectedTemplate.header.format.toLowerCase()}
                            </label>
                            <input
                              type="file"
                              onChange={handleHeaderFileChange}
                              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-primary-600 file:text-white hover:file:bg-primary-700 cursor-pointer"
                            />
                            {errors.headerFile && (
                              <p className="text-red-500 text-xs mt-2 font-medium">
                                {errors.headerFile}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Header Dynamic Params */}
                        {Object.keys(headerParams).map(k => (
                          <div key={`h-${k}`}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Header Variable {'{{' + k + '}}'}
                            </label>
                            <input
                              type="text"
                              className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder:text-gray-400"
                              placeholder={`Value for {{${k}}}`}
                              value={headerParams[k]}
                              onChange={e =>
                                setHeaderParams(prev => ({
                                  ...prev,
                                  [k]: e.target.value,
                                }))
                              }
                            />
                            {errors[`headerParam_${k}`] && (
                              <p className="text-red-500 text-xs mt-1 font-medium">
                                {errors[`headerParam_${k}`]}
                              </p>
                            )}
                          </div>
                        ))}

                        {/* Body Dynamic Params */}
                        {Object.keys(bodyParams).map(k => (
                          <div key={`b-${k}`}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Body Variable {'{{' + k + '}}'}
                            </label>
                            <input
                              type="text"
                              className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder:text-gray-400"
                              placeholder={`Value for {{${k}}}`}
                              value={bodyParams[k]}
                              onChange={e =>
                                setBodyParams(prev => ({
                                  ...prev,
                                  [k]: e.target.value,
                                }))
                              }
                            />
                            {errors[`bodyParam_${k}`] && (
                              <p className="text-red-500 text-xs mt-1 font-medium">
                                {errors[`bodyParam_${k}`]}
                              </p>
                            )}
                          </div>
                        ))}

                        {/* Button Dynamic Params */}
                        {Object.keys(buttonParams).map(k => (
                          <div key={`btn-${k}`}>
                            <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                              {k.split('_')[0].replace('-', ' ')} Variable
                            </label>
                            <input
                              type="text"
                              className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder:text-gray-400"
                              placeholder={`Value for button`}
                              value={buttonParams[k]}
                              onChange={e =>
                                setButtonParams(prev => ({
                                  ...prev,
                                  [k]: e.target.value,
                                }))
                              }
                            />
                            {errors[`buttonParam_${k}`] && (
                              <p className="text-red-500 text-xs mt-1 font-medium">
                                {errors[`buttonParam_${k}`]}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Premium WhatsApp Preview */}
            <div className="md:w-2/5 bg-gray-50 flex flex-col items-center justify-center p-6 bg-texture">
              <TemplatePreview
                showMobilePreview={true}
                template={selectedTemplate}
                headerParams={headerParams}
                bodyParams={bodyParams}
                buttonParams={buttonParams}
                headerFile={headerFile}
                businessName={whatsappConfig?.verifiedName}
                businessLogo={whatsappConfig?.profilePictureUrl}
              />

              <div className="mt-6 flex flex-col items-center">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-widest mb-2">
                  Live Preview
                </p>
                <div className="flex space-x-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse delay-150"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse delay-300"></span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="bg-white px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row-reverse gap-3">
            <button
              type="button"
              disabled={!selectedTemplate || isSubmitting}
              className="w-full sm:w-auto inline-flex justify-center items-center px-8 py-3 bg-primary-600 text-white text-sm font-bold rounded-xl shadow-lg shadow-primary-500/30 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSubmit}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending...
                </>
              ) : (
                'Send Template'
              )}
            </button>
            <button
              type="button"
              className="w-full sm:w-auto inline-flex justify-center items-center px-8 py-3 bg-white text-gray-700 text-sm font-bold rounded-xl border border-gray-200 hover:bg-gray-50 transition-all"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-texture {
          background-image: radial-gradient(#6366f1 0.5px, transparent 0.5px);
          background-size: 10px 10px;
          background-color: #f8fafc;
        }
        .message-content strong {
          font-weight: 700;
        }
        .message-content em {
          font-style: italic;
        }
        .message-content del {
          text-decoration: line-through;
        }
        .message-content code {
          background: #f0f0f0;
          padding: 0 2px;
          border-radius: 2px;
          font-family: monospace;
        }
      `}</style>
    </div>
  );
};

export default SendTemplateModal;
